import { defineStore } from "pinia";
import { ref } from "vue";
import { useToast } from "vue-toastification";
import { httpRequest } from "@follaw-fr/utils/request.utils";
import { handleNetworkError } from "@follaw-fr/utils/handleNetworkError";
import { useSetupCrisp } from "../composables/useSetupCrisp";
import { PermissionLevel } from "../constants/enums/permission-level.enum";

const toast = useToast();
export const useUserSettingsStore = defineStore("userSettingsStore", () => {
  const user = ref(null);
  const loadingFetch = ref(false);
  const loadingUpdate = ref(false);

  const hasPermission = (permissionLevelToTest) => {
    if (!user.value || !user.value.permission_level) {
      return false;
    }

    if (permissionLevelToTest === PermissionLevel.FREE) {
      return true;
    }

    if (permissionLevelToTest === PermissionLevel.BASIC) {
      return (
        user.value.permission_level === PermissionLevel.BASIC ||
        user.value.permission_level === PermissionLevel.TEAMS_BASIC ||
        user.value.permission_level === PermissionLevel.SOCIETAL ||
        user.value.permission_level === PermissionLevel.TEAMS_SOCIETAL ||
        user.value.permission_level === PermissionLevel.TRIAL
      );
    }

    if (permissionLevelToTest === PermissionLevel.SOCIETAL) {
      return (
        user.value.permission_level === PermissionLevel.SOCIETAL ||
        user.value.permission_level === PermissionLevel.TEAMS_SOCIETAL ||
        user.value.permission_level === PermissionLevel.TRIAL
      );
    }

    return true;
  };

  async function getUserSettings() {
    loadingFetch.value = true;

    try {
      const response = await httpRequest("get", "/users/user-profile");
      if (response.data) {
        user.value = response.data;
        const { setupCrisp } = useSetupCrisp();
        setupCrisp(user.value);
      }
    } catch (err) {
      handleNetworkError(err);
    } finally {
      loadingFetch.value = false;
    }
  }

  async function updateUserSettings(payload) {
    loadingUpdate.value = true;

    if (!payload) {
      return;
    }

    if (payload.email) {
      // prevent users from changing their email
      delete payload.email;
    }

    try {
      const response = await httpRequest("patch", "/users", payload);
      if (response.status === 200) {
        user.value = {
          ...user.value,
          ...payload,
          ...response.data,
        };
      }

      toast.success("Vos informations ont été mises à jour");
    } catch (err) {
      handleNetworkError(err);
    }
    loadingUpdate.value = false;
  }

  async function redirectToStripePricePage(planName) {
    try {
      let planUrl = "";
      if (planName === "FREE") {
        planUrl = user.value.stripe_customer_portal_url;
      } else {
        const response = await httpRequest("post", "/create-checkout", {
          plan_name: planName,
        });
        planUrl = response.data.redirect_url;
      }

      window.open(planUrl, "_blank").focus();
    } catch (err) {
      if (
        err.response?.status === 403 &&
        err.response.dataMsg.match(/SV employees.*not allowed/i)
      ) {
        toast.error(err.response.dataMsg);
      } else {
        handleNetworkError(err);
      }
    }
  }

  return {
    user,
    loadingFetch,
    loadingUpdate,
    getUserSettings,
    hasPermission,
    redirectToStripePricePage,
    updateUserSettings,
  };
});
